/**
 * Contenu didactique du questionnaire de diagnostic d'un site.
 *
 * Source de vérité de l'AFFICHAGE (textes, aides, images). La logique de
 * génération des propositions vit côté backend dans
 * `backend/src/data/questionnaire.js`. Contrat partagé = les clés de question.
 *
 * Chaque texte est un objet { fr, en, pt, mg }. Le français est la langue de
 * référence (relue) ; en/pt/mg sont à valider par les provinces.
 *
 * IMPORTANT : QUESTIONNAIRE_VERSION doit rester identique côté backend.
 */

export const QUESTIONNAIRE_VERSION = '1.0';

export const ANSWER_VALUES = ['oui', 'non', 'je_ne_sais_pas'];

// Libellés des 3 boutons de réponse
export const ANSWER_LABELS = {
  oui: { fr: 'Oui', en: 'Yes', pt: 'Sim', mg: 'Eny' },
  non: { fr: 'Non', en: 'No', pt: 'Não', mg: 'Tsia' },
  je_ne_sais_pas: { fr: 'Je ne sais pas', en: "I don't know", pt: 'Não sei', mg: 'Tsy fantatro' }
};

export const CHAPTERS = [
  {
    thematique: 'reseaux',
    intro: {
      fr: "Parlons maintenant de l'électricité, de l'eau et de l'aération. Quelques questions simples sur ce que vous observez au quotidien.",
      en: "Let's talk about electricity, water and ventilation. A few simple questions about what you notice day to day.",
      pt: "Vamos falar de eletricidade, água e ventilação. Algumas perguntas simples sobre o que observa no dia a dia.",
      mg: "Andeha hiresaka momba ny herinaratra, ny rano sy ny fakan-drivotra. Fanontaniana tsotra momba izay hitanao isan'andro."
    },
    questions: [
      {
        key: 'reseaux_elec_01',
        image: 'reseaux/prise-brulee.jpg',
        question: {
          fr: "Avez-vous déjà vu une prise ou un interrupteur noirci, ou senti une odeur de brûlé près d'un appareil électrique ?",
          en: "Have you ever seen a blackened socket or switch, or smelled burning near an electrical device?",
          pt: "Já viu uma tomada ou interruptor enegrecido, ou sentiu cheiro a queimado perto de um aparelho elétrico?",
          mg: "Efa nahita fitsofana na interrupteur mainty, na nanimbolo may akaikin'ny fitaovana elektrika ve ianao?"
        },
        why: {
          fr: "Une prise qui chauffe, noircit ou sent le brûlé est un signe de danger électrique sérieux, avec un risque d'incendie.",
          en: "A socket that heats up, blackens or smells of burning is a sign of serious electrical danger, with a fire risk.",
          pt: "Uma tomada que aquece, enegrece ou cheira a queimado é sinal de perigo elétrico sério, com risco de incêndio.",
          mg: "Ny fitsofana mafana, mainty na manimbolo may dia famantarana loza elektrika lehibe, misy risika afo."
        },
        what: {
          fr: "Les prises les plus utilisées : cuisine, chauffages d'appoint, multiprises surchargées.",
          en: "The most-used sockets: kitchen, portable heaters, overloaded power strips.",
          pt: "As tomadas mais usadas: cozinha, aquecedores portáteis, extensões sobrecarregadas.",
          mg: "Ny fitsofana be ampiasaina: lakozia, fanafanana mivezivezy, multiprises feno loatra."
        }
      },
      {
        key: 'reseaux_elec_02',
        image: 'reseaux/fils-denudes.jpg',
        question: {
          fr: "Y a-t-il des fils électriques dénudés, apparents, ou des rallonges installées en permanence ?",
          en: "Are there bare or exposed electrical wires, or permanently installed extension cords?",
          pt: "Há fios elétricos desencapados, à vista, ou extensões instaladas de forma permanente?",
          mg: "Misy tariby elektrika mihoboka, miharihary, na rallonge mitoetra hatrany ve?"
        },
        why: {
          fr: "Des fils abîmés ou des rallonges permanentes exposent à l'électrocution et au court-circuit.",
          en: "Damaged wires or permanent extension cords expose you to electrocution and short circuits.",
          pt: "Fios danificados ou extensões permanentes expõem a eletrocussão e curto-circuito.",
          mg: "Ny tariby simba na rallonge maharitra dia mampidi-doza amin'ny herinaratra sy ny court-circuit."
        },
        what: {
          fr: "Les angles de murs, derrière les meubles, les fils qui passent sous les tapis.",
          en: "Wall corners, behind furniture, wires running under rugs.",
          pt: "Os cantos das paredes, atrás dos móveis, fios que passam debaixo dos tapetes.",
          mg: "Ny zorony rindrina, ao ambadiky ny fanaka, ny tariby mandalo ambany karipetra."
        }
      },
      {
        key: 'reseaux_elec_03',
        image: 'reseaux/disjoncteur.jpg',
        question: {
          fr: "L'électricité disjoncte-t-elle souvent (le courant se coupe tout seul) ?",
          en: "Does the electricity trip often (the power cuts out by itself)?",
          pt: "A eletricidade dispara com frequência (a energia corta-se sozinha)?",
          mg: "Matetika ve mihidy ny herinaratra (tapaka ho azy ny jiro)?"
        },
        why: {
          fr: "Des coupures fréquentes signalent une installation surchargée ou défectueuse à faire diagnostiquer.",
          en: "Frequent cut-outs indicate an overloaded or faulty installation that should be checked.",
          pt: "Cortes frequentes indicam uma instalação sobrecarregada ou defeituosa a verificar.",
          mg: "Ny fahatapahana matetika dia maneho fametrahana feno loatra na simba tokony hojerena."
        },
        what: {
          fr: "Le tableau électrique : un interrupteur qui « saute » régulièrement.",
          en: "The electrical panel: a breaker that keeps tripping.",
          pt: "O quadro elétrico: um disjuntor que «salta» regularmente.",
          mg: "Ny tableau elektrika: interrupteur izay 'mitsambikina' matetika."
        }
      },
      {
        key: 'reseaux_plomb_01',
        image: 'reseaux/fuite-eau.jpg',
        question: {
          fr: "Voyez-vous des traces d'eau, des gouttes ou des tuyaux qui suintent sous un évier, dans une cave ou un placard ?",
          en: "Do you see water marks, drips or seeping pipes under a sink, in a cellar or a cupboard?",
          pt: "Vê marcas de água, gotas ou canos a pingar sob um lava-loiça, numa cave ou armário?",
          mg: "Mahita marika rano, ranonorana na fantsona mitete ambanin'ny evier, anaty lava-bato na lalomoara ve ianao?"
        },
        why: {
          fr: "Une fuite, même petite, abîme le bâtiment et fait grimper la facture d'eau si on attend.",
          en: "A leak, even a small one, damages the building and raises the water bill if left.",
          pt: "Uma fuga, mesmo pequena, danifica o edifício e aumenta a fatura da água se ignorada.",
          mg: "Ny fitsoahana, na dia kely aza, dia manimba ny trano ary mampiakatra ny faktioran-drano raha tsy raharahaina."
        },
        what: {
          fr: "Sous les éviers, autour des WC, au plafond des caves, le compteur d'eau qui tourne sans usage.",
          en: "Under sinks, around toilets, on cellar ceilings, the water meter running with no use.",
          pt: "Sob os lava-loiças, à volta das sanitas, no teto das caves, o contador de água a rodar sem uso.",
          mg: "Ambanin'ny evier, manodidina ny WC, plafond lava-bato, ny compteur rano mihodina nefa tsy misy mampiasa."
        }
      },
      {
        key: 'reseaux_chauf_01',
        image: 'reseaux/aeration.jpg',
        question: {
          fr: "Y a-t-il des pièces mal aérées, où l'air semble stagner (humidité, condensation, odeur de renfermé) ?",
          en: "Are there poorly ventilated rooms where the air seems to stagnate (damp, condensation, musty smell)?",
          pt: "Há divisões mal ventiladas onde o ar parece estagnar (humidade, condensação, cheiro a mofo)?",
          mg: "Misy efitrano tsy misy rivotra tsara, toa mijanona ny rivotra (hamandoana, condensation, fofona lo) ve?"
        },
        why: {
          fr: "Un air qui ne se renouvelle pas favorise l'humidité, les moisissures et nuit à la santé, surtout en climat chaud ou humide.",
          en: "Air that doesn't renew encourages damp and mould and harms health, especially in hot or humid climates.",
          pt: "O ar que não se renova favorece a humidade e o bolor e prejudica a saúde, sobretudo em climas quentes ou húmidos.",
          mg: "Ny rivotra tsy mihavao dia mampitombo ny hamandoana sy ny bobongolo ary manimba ny fahasalamana, indrindra amin'ny toetrandro mafana na mando."
        },
        what: {
          fr: "Les fenêtres qui ne s'ouvrent plus, les grilles d'aération bouchées, les pièces toujours humides.",
          en: "Windows that no longer open, blocked vents, rooms that are always damp.",
          pt: "Janelas que já não abrem, grelhas de ventilação obstruídas, divisões sempre húmidas.",
          mg: "Ny varavarankely tsy misokatra intsony, ny grille fakan-drivotra tampina, ny efitrano mando hatrany."
        }
      }
    ]
  },
  {
    thematique: 'gros_oeuvre',
    intro: {
      fr: "Regardons maintenant la solidité générale du bâtiment : les murs, le toit, les sols et les escaliers.",
      en: "Now let's look at the building's overall soundness: walls, roof, floors and stairs.",
      pt: "Vejamos agora a solidez geral do edifício: paredes, telhado, pisos e escadas.",
      mg: "Andeha hojerentsika ny fahamafisan'ny trano: ny rindrina, ny tafo, ny gorodona sy ny tohatra."
    },
    questions: [
      {
        key: 'gros_murs_01',
        image: 'gros_oeuvre/fissure-escalier.jpg',
        question: {
          fr: "Voyez-vous des fissures sur les murs, larges comme un doigt, ou en forme d'escalier ?",
          en: "Do you see cracks on the walls, as wide as a finger, or in a staircase shape?",
          pt: "Vê fissuras nas paredes, largas como um dedo, ou em forma de escada?",
          mg: "Mahita triatra eo amin'ny rindrina, midadasika toy ny rantsantanana, na miendrika tohatra ve ianao?"
        },
        why: {
          fr: "Une fissure large ou en escalier peut signaler un mouvement de la structure, à faire examiner sans tarder.",
          en: "A wide or staircase crack may signal structural movement and should be examined promptly.",
          pt: "Uma fissura larga ou em escada pode indicar movimento estrutural, a examinar sem demora.",
          mg: "Ny triatra midadasika na miendrika tohatra dia mety maneho fihetsiky ny rafitra, tokony hojerena haingana."
        },
        what: {
          fr: "Les murs porteurs, au-dessus des portes et fenêtres, les angles de la maison.",
          en: "Load-bearing walls, above doors and windows, the corners of the house.",
          pt: "As paredes mestras, acima de portas e janelas, os cantos da casa.",
          mg: "Ny rindrina mitondra, ambonin'ny varavarana sy varavarankely, ny zoron'ny trano."
        }
      },
      {
        key: 'gros_murs_02',
        image: 'gros_oeuvre/facade-degradee.jpg',
        question: {
          fr: "La façade présente-t-elle des morceaux qui se détachent, du crépi qui tombe ou des pierres descellées ?",
          en: "Does the façade have pieces coming off, falling render or loose stones?",
          pt: "A fachada tem pedaços a soltar-se, reboco a cair ou pedras soltas?",
          mg: "Misy potika miala, crépi mianjera na vato mivaha ve ny façade?"
        },
        why: {
          fr: "Des éléments qui se détachent en hauteur peuvent blesser quelqu'un et s'aggravent avec le gel et la pluie.",
          en: "Pieces coming off at height can injure someone and worsen with frost and rain.",
          pt: "Elementos que se soltam em altura podem ferir alguém e agravam-se com a geada e a chuva.",
          mg: "Ny singa miala any amin'ny avo dia mety mandratra olona ary mihalehibe noho ny fanala sy ny orana."
        },
        what: {
          fr: "Le bas des façades, les corniches, les rebords de fenêtres.",
          en: "The bottom of façades, cornices, window ledges.",
          pt: "A base das fachadas, as cornijas, os parapeitos das janelas.",
          mg: "Ny ambany façade, ny corniche, ny sisin'ny varavarankely."
        }
      },
      {
        key: 'gros_toit_01',
        image: 'gros_oeuvre/fuite-toit.jpg',
        question: {
          fr: "Lors de fortes pluies, y a-t-il des fuites par le toit ou des taches d'humidité au plafond ?",
          en: "During heavy rain, are there roof leaks or damp stains on the ceiling?",
          pt: "Durante chuvas fortes, há infiltrações pelo telhado ou manchas de humidade no teto?",
          mg: "Mandritra ny orana be, misy mitete avy amin'ny tafo na pentina mando eo amin'ny plafond ve?"
        },
        why: {
          fr: "Une toiture qui fuit abîme la charpente et les plafonds ; le coût grimpe vite si on attend.",
          en: "A leaking roof damages the frame and ceilings; the cost rises quickly if left.",
          pt: "Um telhado com infiltrações danifica a estrutura e os tetos; o custo sobe rápido se ignorado.",
          mg: "Ny tafo mitete dia manimba ny charpente sy ny plafond; mitombo haingana ny vidiny raha tsy raharahaina."
        },
        what: {
          fr: "Les plafonds des derniers étages, les combles, les taches brunes qui s'agrandissent.",
          en: "Top-floor ceilings, the attic, brown stains that keep growing.",
          pt: "Os tetos dos últimos andares, o sótão, manchas castanhas que aumentam.",
          mg: "Ny plafond amin'ny rihana ambony, ny combles, ny pentina volontany mihalehibe."
        }
      },
      {
        key: 'gros_toit_02',
        image: 'gros_oeuvre/tuiles-tombees.jpg',
        question: {
          fr: "Des éléments de toiture (tuiles, tôles, dalles) sont-ils tombés récemment, ou la toiture semble-t-elle affaissée ?",
          en: "Have roof elements (tiles, metal sheets, slabs) fallen recently, or does the roof look sagging?",
          pt: "Caíram elementos do telhado (telhas, chapas, lajes) recentemente, ou o telhado parece afundado?",
          mg: "Nisy singan'ny tafo (tuiles, takela-by, dalle) latsaka vao haingana ve, na toa milentika ny tafo?"
        },
        why: {
          fr: "Des éléments de toiture qui tombent sont un danger, et un affaissement signale une charpente fragilisée.",
          en: "Falling roof elements are a danger, and sagging signals a weakened frame.",
          pt: "Elementos do telhado a cair são um perigo, e o afundamento indica uma estrutura enfraquecida.",
          mg: "Ny singan'ny tafo mianjera dia loza, ary ny filentehana dia maneho charpente malemy."
        },
        what: {
          fr: "Le sol au pied du bâtiment après une tempête, la ligne du toit qui doit rester droite.",
          en: "The ground at the foot of the building after a storm; the roofline should stay straight.",
          pt: "O chão ao pé do edifício após uma tempestade; a linha do telhado deve manter-se direita.",
          mg: "Ny tany eo am-pototry ny trano aorian'ny tafio-drivotra, ny tsipiky ny tafo tokony hijanona mahitsy."
        }
      },
      {
        key: 'gros_sol_01',
        image: 'gros_oeuvre/sol-affaisse.jpg',
        question: {
          fr: "Y a-t-il des sols qui s'affaissent, des carrelages qui se soulèvent ou des planchers qui « sonnent creux » ?",
          en: "Are there sinking floors, lifting tiles or floorboards that sound hollow?",
          pt: "Há pisos que afundam, azulejos que se levantam ou soalhos que «soam ocos»?",
          mg: "Misy gorodona milentika, carrelage misondrotra na parquet 'maneno poakaty' ve?"
        },
        why: {
          fr: "Un sol qui bouge peut révéler un problème de fondation ou d'humidité sous le bâtiment.",
          en: "A moving floor can reveal a foundation or damp problem under the building.",
          pt: "Um piso que se move pode revelar um problema de fundação ou humidade sob o edifício.",
          mg: "Ny gorodona mihetsika dia mety maneho olana amin'ny fototra na hamandoana ambanin'ny trano."
        },
        what: {
          fr: "Les sols du rez-de-chaussée, les seuils de porte, les zones près des murs extérieurs.",
          en: "Ground-floor floors, door thresholds, areas near outer walls.",
          pt: "Os pisos do rés-do-chão, as soleiras das portas, as zonas junto às paredes exteriores.",
          mg: "Ny gorodona amin'ny rez-de-chaussée, ny tokonam-baravarana, ny faritra akaikin'ny rindrina ivelany."
        }
      },
      {
        key: 'gros_esc_01',
        image: 'gros_oeuvre/rampe-escalier.jpg',
        question: {
          fr: "Une rampe ou un garde-corps d'escalier est-il branlant, cassé ou manquant ?",
          en: "Is a stair handrail or guardrail wobbly, broken or missing?",
          pt: "Um corrimão ou guarda de escada está bambo, partido ou em falta?",
          mg: "Misy rampe na garde-corps amin'ny tohatra mihozongozona, vaky na tsy misy ve?"
        },
        why: {
          fr: "Une rampe qui lâche provoque des chutes graves, surtout pour les personnes âgées.",
          en: "A failing handrail causes serious falls, especially for the elderly.",
          pt: "Um corrimão que cede provoca quedas graves, sobretudo para os idosos.",
          mg: "Ny rampe miala dia miteraka fianjerana mafy, indrindra ho an'ny zokiolona."
        },
        what: {
          fr: "Secouez doucement les rampes ; vérifiez les escaliers les plus empruntés et les balcons.",
          en: "Gently shake the rails; check the most-used stairs and balconies.",
          pt: "Abane suavemente os corrimãos; verifique as escadas mais usadas e as varandas.",
          mg: "Ahozongozony moramora ny rampe; jereo ny tohatra be ampiasaina sy ny balcon."
        }
      }
    ]
  },
  {
    thematique: 'incendie',
    intro: {
      fr: "Parlons de votre sécurité : la protection contre le feu et la fermeture des locaux.",
      en: "Let's talk about your safety: fire protection and securing the premises.",
      pt: "Falemos da sua segurança: a proteção contra incêndios e o fecho das instalações.",
      mg: "Andeha hiresaka momba ny fiarovana anao: ny fiarovana amin'ny afo sy ny fanidiana ny toerana."
    },
    questions: [
      {
        key: 'incendie_det_01',
        image: 'incendie/detecteur-fumee.jpg',
        question: {
          fr: "Y a-t-il des détecteurs de fumée installés, et avez-vous entendu leur signal de test récemment ?",
          en: "Are smoke detectors installed, and have you heard their test signal recently?",
          pt: "Há detetores de fumo instalados e ouviu o seu sinal de teste recentemente?",
          mg: "Misy detecteur setroka napetraka ve, ary efa nandre ny feon'ny test-ny vao haingana ve ianao?"
        },
        why: {
          fr: "Les détecteurs de fumée sauvent des vies en alertant tôt ; ils sont obligatoires dans les lieux d'habitation.",
          en: "Smoke detectors save lives by warning early; they are mandatory in dwellings.",
          pt: "Os detetores de fumo salvam vidas ao alertar cedo; são obrigatórios em habitações.",
          mg: "Ny detecteur setroka dia mamonjy aina amin'ny fampandrenesana aloha; tsy maintsy ao amin'ny fonenana."
        },
        what: {
          fr: "Au plafond des couloirs et chambres ; le petit voyant clignotant et le bouton de test.",
          en: "On corridor and bedroom ceilings; the small blinking light and the test button.",
          pt: "No teto dos corredores e quartos; a pequena luz a piscar e o botão de teste.",
          mg: "Eo amin'ny plafond-ny lalantsara sy efitrano; ny jiro kely mipi-maso sy ny bokotra test."
        }
      },
      {
        key: 'incendie_ext_01',
        image: 'incendie/extincteur.jpg',
        question: {
          fr: "Les extincteurs sont-ils présents, accessibles et en bon état (vérifiés récemment) ?",
          en: "Are extinguishers present, accessible and in good condition (checked recently)?",
          pt: "Os extintores estão presentes, acessíveis e em bom estado (verificados recentemente)?",
          mg: "Misy, mora idirana ary tsara toetra (voamarina vao haingana) ve ny extincteur?"
        },
        why: {
          fr: "Un extincteur absent, bloqué ou non vérifié ne fonctionnera pas le jour où il faut.",
          en: "An extinguisher that is missing, blocked or unchecked won't work when needed.",
          pt: "Um extintor ausente, bloqueado ou não verificado não funcionará quando for preciso.",
          mg: "Ny extincteur tsy eo, voasakana na tsy voamarina dia tsy handeha rehefa ilaina."
        },
        what: {
          fr: "L'extincteur ne doit pas être caché ni encombré ; l'étiquette indique la date du dernier contrôle.",
          en: "The extinguisher must not be hidden or blocked; the label shows the last inspection date.",
          pt: "O extintor não deve estar escondido nem obstruído; a etiqueta indica a data da última inspeção.",
          mg: "Tsy tokony hafenina na voatampina ny extincteur; ny étiquette dia manondro ny daty fanaraha-maso farany."
        }
      },
      {
        key: 'incendie_alarme_01',
        image: 'incendie/alarme.jpg',
        question: {
          fr: "Existe-t-il une alarme incendie audible dans tout le bâtiment ?",
          en: "Is there a fire alarm audible throughout the building?",
          pt: "Existe um alarme de incêndio audível em todo o edifício?",
          mg: "Misy alarma afo re manerana ny trano manontolo ve?"
        },
        why: {
          fr: "En cas de feu, tout le monde doit pouvoir être alerté rapidement, même à l'étage ou dans une aile éloignée.",
          en: "In a fire, everyone must be alerted quickly, even upstairs or in a far wing.",
          pt: "Num incêndio, todos devem ser alertados rapidamente, mesmo no andar ou numa ala afastada.",
          mg: "Raha misy afo, tokony ho fantatry ny rehetra haingana, na any an-tampony na any amin'ny elatra lavitra."
        },
        what: {
          fr: "Les boîtiers d'alarme, les sirènes ; demandez si un test a déjà été fait.",
          en: "Alarm boxes, sirens; ask whether a test has ever been done.",
          pt: "As caixas de alarme, as sirenes; pergunte se já foi feito um teste.",
          mg: "Ny boaty alarma, ny sirène; anontanio raha efa nisy test natao."
        }
      },
      {
        key: 'incendie_issue_01',
        image: 'incendie/issue-secours.jpg',
        question: {
          fr: "Les issues de secours et couloirs sont-ils dégagés et faciles à ouvrir de l'intérieur ?",
          en: "Are the emergency exits and corridors clear and easy to open from inside?",
          pt: "As saídas de emergência e corredores estão desimpedidos e fáceis de abrir por dentro?",
          mg: "Malalaka sy mora sokafana avy ao anatiny ve ny fivoahana vonjy maika sy ny lalantsara?"
        },
        why: {
          fr: "Une sortie bloquée ou difficile à ouvrir empêche d'évacuer en cas d'urgence.",
          en: "A blocked or hard-to-open exit prevents evacuation in an emergency.",
          pt: "Uma saída bloqueada ou difícil de abrir impede a evacuação numa emergência.",
          mg: "Ny fivoahana voasakana na sarotra sokafana dia misakana ny fivoahana rehefa misy loza."
        },
        what: {
          fr: "Les portes de sortie encombrées par des meubles, fermées à clé sans ouverture facile de l'intérieur.",
          en: "Exit doors blocked by furniture, locked without easy opening from inside.",
          pt: "Portas de saída obstruídas por móveis, trancadas sem abertura fácil por dentro.",
          mg: "Ny varavarana fivoahana voatampina fanaka, mihidy nefa tsy mora sokafana avy ao anatiny."
        }
      },
      {
        key: 'incendie_ferm_01',
        image: 'incendie/serrure.jpg',
        question: {
          fr: "Les portes et fenêtres du rez-de-chaussée ferment-elles correctement à clé ?",
          en: "Do the ground-floor doors and windows lock properly?",
          pt: "As portas e janelas do rés-do-chão fecham corretamente à chave?",
          mg: "Mihidy tsara amin'ny lakile ve ny varavarana sy varavarankely amin'ny rez-de-chaussée?"
        },
        why: {
          fr: "Une fermeture défaillante expose au vol et à l'intrusion, un risque pour la sécurité des personnes et des biens.",
          en: "Faulty locking exposes to theft and intrusion, a risk to people and property.",
          pt: "Um fecho deficiente expõe a roubo e intrusão, um risco para pessoas e bens.",
          mg: "Ny fanidiana simba dia mampidi-doza amin'ny halatra sy fidirana an-keriny, risika ho an'ny olona sy fananana."
        },
        what: {
          fr: "Les serrures forcées ou usées, les fenêtres qui ne se verrouillent plus, les portes voilées.",
          en: "Forced or worn locks, windows that no longer latch, warped doors.",
          pt: "Fechaduras forçadas ou gastas, janelas que já não trancam, portas empenadas.",
          mg: "Ny hidin-trano voatery na simba, ny varavarankely tsy mihidy intsony, ny varavarana miolaka."
        }
      }
    ]
  },
  {
    thematique: 'incidents',
    intro: {
      fr: "Quelques signes visibles d'usure qui méritent attention avant qu'ils ne s'aggravent.",
      en: "A few visible signs of wear worth attention before they worsen.",
      pt: "Alguns sinais visíveis de desgaste a observar antes que se agravem.",
      mg: "Famantarana fahasimbana hita maso vitsivitsy tokony hojerena alohan'ny hiharatsiany."
    },
    questions: [
      {
        key: 'incidents_infil_01',
        image: 'incidents/infiltration.jpg',
        question: {
          fr: "Voyez-vous des traces d'humidité, des auréoles ou de l'eau qui s'infiltre par un mur ou une fenêtre ?",
          en: "Do you see damp marks, halos or water seeping in through a wall or window?",
          pt: "Vê marcas de humidade, auréolas ou água a infiltrar-se por uma parede ou janela?",
          mg: "Mahita marika hamandoana, faribolana na rano miditra avy amin'ny rindrina na varavarankely ve ianao?"
        },
        why: {
          fr: "Une infiltration non traitée abîme les murs, favorise les moisissures et peut atteindre l'électricité.",
          en: "Untreated seepage damages walls, breeds mould and can reach the electrics.",
          pt: "Uma infiltração não tratada danifica as paredes, favorece o bolor e pode atingir a eletricidade.",
          mg: "Ny fidiran-drano tsy voakarakara dia manimba ny rindrina, mampitombo bobongolo ary mety hahatratra ny herinaratra."
        },
        what: {
          fr: "Le tour des fenêtres, le bas des murs extérieurs, les caves après la pluie.",
          en: "Around windows, the bottom of outer walls, cellars after rain.",
          pt: "À volta das janelas, a base das paredes exteriores, as caves após a chuva.",
          mg: "Manodidina ny varavarankely, ny ambany rindrina ivelany, ny lava-bato aorian'ny orana."
        }
      },
      {
        key: 'incidents_fiss_01',
        image: 'incidents/fissure-fine.jpg',
        question: {
          fr: "Remarquez-vous de fines fissures qui s'agrandissent avec le temps sur les murs ou plafonds ?",
          en: "Do you notice fine cracks growing over time on walls or ceilings?",
          pt: "Nota fissuras finas que aumentam com o tempo nas paredes ou tetos?",
          mg: "Mahatsikaritra triatra madinika mihalehibe arakaraka ny fotoana eo amin'ny rindrina na plafond ve ianao?"
        },
        why: {
          fr: "Une fissure qui évolue mérite une surveillance ; elle peut être bénigne ou annoncer un problème.",
          en: "A growing crack warrants monitoring; it may be harmless or signal a problem.",
          pt: "Uma fissura que evolui merece vigilância; pode ser inofensiva ou anunciar um problema.",
          mg: "Ny triatra mivoatra dia tokony harahi-maso; mety tsy maninona na maneho olana."
        },
        what: {
          fr: "Marquez la fissure au crayon avec une date ; voyez si elle s'allonge sur quelques mois.",
          en: "Mark the crack in pencil with a date; see if it lengthens over a few months.",
          pt: "Marque a fissura a lápis com uma data; veja se aumenta ao longo de alguns meses.",
          mg: "Mariho amin'ny pensilihazo miaraka amin'ny daty ny triatra; jereo raha mihalava mandritra ny volana vitsivitsy."
        }
      },
      {
        key: 'incidents_peint_01',
        image: 'incidents/peinture-ecaillee.jpg',
        question: {
          fr: "La peinture s'écaille-t-elle ou cloque-t-elle sur certains murs ou plafonds ?",
          en: "Is the paint flaking or blistering on some walls or ceilings?",
          pt: "A tinta está a descascar ou a empolar em algumas paredes ou tetos?",
          mg: "Mihintsana na mivonto ve ny loko amin'ny rindrina na plafond sasany?"
        },
        why: {
          fr: "Une peinture qui cloque traduit souvent un excès d'humidité derrière le mur.",
          en: "Blistering paint often reflects excess moisture behind the wall.",
          pt: "Tinta a empolar traduz muitas vezes excesso de humidade atrás da parede.",
          mg: "Ny loko mivonto matetika dia maneho hamandoana be ao ambadiky ny rindrina."
        },
        what: {
          fr: "Les pièces humides (salle de bain, cuisine), les murs exposés au nord.",
          en: "Damp rooms (bathroom, kitchen), north-facing walls.",
          pt: "As divisões húmidas (casa de banho, cozinha), as paredes orientadas a norte.",
          mg: "Ny efitrano mando (efitra fandroana, lakozia), ny rindrina mitodika avaratra."
        }
      },
      {
        key: 'incidents_struct_01',
        image: 'incidents/zone-condamnee.jpg',
        question: {
          fr: "Une partie du bâtiment vous semble-t-elle dangereuse ou interdite d'accès par précaution ?",
          en: "Does a part of the building seem dangerous or closed off as a precaution?",
          pt: "Alguma parte do edifício lhe parece perigosa ou interditada por precaução?",
          mg: "Misy faritra amin'ny trano toa mampidi-doza na voarara hidirana noho ny fitandremana ve?"
        },
        why: {
          fr: "Une zone qu'on évite « par sécurité » cache souvent une dégradation structurelle à expertiser.",
          en: "An area avoided 'for safety' often hides structural damage needing expert review.",
          pt: "Uma zona evitada «por segurança» esconde muitas vezes uma degradação estrutural a peritar.",
          mg: "Ny faritra ialana 'noho ny filaminana' matetika dia manafina fahasimbana rafitra tokony hodinihina."
        },
        what: {
          fr: "Les pièces condamnées, planchers évités, plafonds qui s'affaissent.",
          en: "Sealed-off rooms, avoided floors, sagging ceilings.",
          pt: "Divisões interditadas, pisos evitados, tetos a afundar.",
          mg: "Ny efitrano voahidy, ny gorodona ialana, ny plafond milentika."
        }
      },
      {
        key: 'incidents_struct_02',
        image: 'incidents/porte-deformee.jpg',
        question: {
          fr: "Une porte ou une fenêtre coince-t-elle de plus en plus, comme si le cadre se déformait ?",
          en: "Is a door or window jamming more and more, as if the frame were warping?",
          pt: "Uma porta ou janela está a emperrar cada vez mais, como se o caixilho se deformasse?",
          mg: "Misy varavarana na varavarankely mitombo fihazonana, toy ny hoe miova endrika ny cadre?"
        },
        why: {
          fr: "Un cadre qui se déforme peut être le signe d'un mouvement du bâtiment.",
          en: "A warping frame can be a sign of building movement.",
          pt: "Um caixilho que se deforma pode ser sinal de movimento do edifício.",
          mg: "Ny cadre miova endrika dia mety famantarana fihetsiky ny trano."
        },
        what: {
          fr: "Les portes qui frottent au sol, les fenêtres difficiles à fermer apparues récemment.",
          en: "Doors scraping the floor, windows newly hard to close.",
          pt: "Portas que raspam no chão, janelas difíceis de fechar surgidas recentemente.",
          mg: "Ny varavarana mikiraro amin'ny gorodona, ny varavarankely sarotra hidiana vao niseho."
        }
      }
    ]
  },
  {
    thematique: 'conformite',
    intro: {
      fr: "Quelques points de sécurité importants pour vivre sereinement dans le bâtiment.",
      en: "A few important safety points for living serenely in the building.",
      pt: "Alguns pontos de segurança importantes para viver com tranquilidade no edifício.",
      mg: "Teboka momba ny filaminana manan-danja vitsivitsy mba hahazoana mipetraka am-pilaminana ao an-trano."
    },
    questions: [
      {
        key: 'conf_elec_01',
        image: 'conformite/tableau-ancien.jpg',
        question: {
          fr: "L'installation électrique vous semble-t-elle sûre et pas trop ancienne (déjà vérifiée par un professionnel) ?",
          en: "Does the electrical installation seem safe and not too old (already checked by a professional)?",
          pt: "A instalação elétrica parece-lhe segura e não demasiado antiga (já verificada por um profissional)?",
          mg: "Toa azo antoka sy tsy tranainy loatra (efa nojeren'ny mpahay) ve ny fametrahana elektrika?"
        },
        why: {
          fr: "Une installation ancienne et jamais vérifiée est l'une des premières causes d'incendie.",
          en: "An old, never-checked installation is one of the leading fire causes.",
          pt: "Uma instalação antiga e nunca verificada é uma das principais causas de incêndio.",
          mg: "Ny fametrahana tranainy sady tsy voamarina mihitsy dia iray amin'ny antony voalohany mahatonga afo."
        },
        what: {
          fr: "L'âge de l'installation, des fils ou un tableau d'aspect très ancien, l'absence de tout contrôle.",
          en: "The age of the installation, very old-looking wiring or panel, the absence of any check.",
          pt: "A idade da instalação, fios ou quadro de aspeto muito antigo, a ausência de qualquer verificação.",
          mg: "Ny taonan'ny fametrahana, tariby na tableau toa tena tranainy, ny tsy fisian'ny fanamarinana mihitsy."
        }
      },
      {
        key: 'conf_incendie_01',
        image: 'conformite/consignes-incendie.jpg',
        question: {
          fr: "En cas d'incendie, les résidentes savent-elles quoi faire et par où sortir ?",
          en: "In case of fire, do the residents know what to do and where to get out?",
          pt: "Em caso de incêndio, as residentes sabem o que fazer e por onde sair?",
          mg: "Raha misy afo, fantatry ny mponina ve izay atao sy ny lalana hivoahana?"
        },
        why: {
          fr: "Des consignes simples connues de tous (sorties, point de rassemblement) sauvent des vies, même sans public.",
          en: "Simple instructions known to everyone (exits, meeting point) save lives, even without public.",
          pt: "Instruções simples conhecidas por todos (saídas, ponto de encontro) salvam vidas, mesmo sem público.",
          mg: "Ny toromarika tsotra fantatry ny rehetra (fivoahana, toerana fivoriana) dia mamonjy aina, na dia tsy misy vahoaka aza."
        },
        what: {
          fr: "L'existence de consignes affichées, d'un point de rassemblement, d'au moins un exercice déjà fait.",
          en: "Posted instructions, a meeting point, at least one drill already done.",
          pt: "Instruções afixadas, um ponto de encontro, pelo menos um exercício já feito.",
          mg: "Ny fisian'ny toromarika apetaka, toerana fivoriana, fanazaran-tena iray farafahakeliny efa natao."
        }
      }
    ]
  },
  {
    thematique: 'pmr',
    intro: {
      fr: "Pensons à celles et ceux qui se déplacent difficilement.",
      en: "Let's think of those who move around with difficulty.",
      pt: "Pensemos naqueles que se deslocam com dificuldade.",
      mg: "Andeha hieritreritra an'ireo sarotra mifindra."
    },
    questions: [
      {
        key: 'pmr_esc_01',
        image: 'pmr/escalier-raide.jpg',
        question: {
          fr: "Y a-t-il des escaliers étroits ou raides, difficiles à emprunter pour une personne âgée ?",
          en: "Are there narrow or steep stairs, hard for an elderly person to use?",
          pt: "Há escadas estreitas ou íngremes, difíceis de usar por uma pessoa idosa?",
          mg: "Misy tohatra tery na mideza, sarotra aleha ho an'ny zokiolona ve?"
        },
        why: {
          fr: "Des escaliers difficiles augmentent fortement le risque de chute dans une communauté vieillissante.",
          en: "Difficult stairs greatly raise the fall risk in an ageing community.",
          pt: "Escadas difíceis aumentam muito o risco de queda numa comunidade envelhecida.",
          mg: "Ny tohatra sarotra dia mampitombo be ny risika fianjerana ao amin'ny fiarahamonina efa antitra."
        },
        what: {
          fr: "Les marches hautes, l'absence de palier de repos, l'éclairage insuffisant.",
          en: "High steps, no resting landing, poor lighting.",
          pt: "Degraus altos, ausência de patamar de descanso, iluminação insuficiente.",
          mg: "Ny tohatra avo, ny tsy fisian'ny palier fialan-tsasatra, ny jiro tsy ampy."
        }
      },
      {
        key: 'pmr_rampe_01',
        image: 'pmr/main-courante.jpg',
        question: {
          fr: "Manque-t-il des rampes ou mains courantes le long des escaliers et couloirs ?",
          en: "Are handrails missing along stairs and corridors?",
          pt: "Faltam corrimãos ao longo das escadas e corredores?",
          mg: "Tsy ampy ve ny rampe na main courante manaraka ny tohatra sy ny lalantsara?"
        },
        why: {
          fr: "Une main courante des deux côtés sécurise les déplacements des personnes fragiles.",
          en: "A handrail on both sides makes movement safer for frail people.",
          pt: "Um corrimão de ambos os lados torna mais seguras as deslocações de pessoas frágeis.",
          mg: "Ny main courante andaniny roa dia manamafy ny fiarovana ny fivezivezen'ny olona malemy."
        },
        what: {
          fr: "Les escaliers sans rampe, les longs couloirs sans appui.",
          en: "Stairs without a rail, long corridors with no support.",
          pt: "As escadas sem corrimão, os corredores longos sem apoio.",
          mg: "Ny tohatra tsy misy rampe, ny lalantsara lava tsy misy fanohanana."
        }
      },
      {
        key: 'pmr_asc_01',
        image: 'pmr/ascenseur.jpg',
        question: {
          fr: "Les personnes à mobilité réduite peuvent-elles accéder aux étages (ascenseur ou logement de plain-pied) ?",
          en: "Can people with reduced mobility reach the upper floors (lift or single-level housing)?",
          pt: "As pessoas com mobilidade reduzida conseguem aceder aos andares (elevador ou alojamento térreo)?",
          mg: "Afaka miakatra any amin'ny rihana ve ny olona malemy fivezivezena (ascenseur na trano iray rihana)?"
        },
        why: {
          fr: "Sans solution d'accès aux étages, certaines résidentes se retrouvent isolées ou en difficulté.",
          en: "Without upper-floor access, some residents end up isolated or struggling.",
          pt: "Sem solução de acesso aos andares, algumas residentes ficam isoladas ou em dificuldade.",
          mg: "Raha tsy misy vahaolana hiakarana, ny mponina sasany dia mitokana na sahirana."
        },
        what: {
          fr: "Présence et bon fonctionnement d'un ascenseur, possibilité de loger les personnes fragiles en bas.",
          en: "A working lift, the option to house frail people downstairs.",
          pt: "Existência e bom funcionamento de um elevador, possibilidade de alojar pessoas frágeis no piso térreo.",
          mg: "Ny fisian'ny ascenseur mandeha tsara, ny fahafahana mametraka ny olona malemy any ambany."
        }
      },
      {
        key: 'pmr_signal_01',
        image: 'pmr/signaletique.jpg',
        question: {
          fr: "Les lieux importants (escaliers, sanitaires, sorties) sont-ils bien indiqués et bien éclairés, y compris la nuit ?",
          en: "Are key places (stairs, toilets, exits) well indicated and well lit, including at night?",
          pt: "Os locais importantes (escadas, sanitários, saídas) estão bem indicados e bem iluminados, incluindo à noite?",
          mg: "Voamarika tsara sy mazava tsara, anatin'izany ny alina, ve ny toerana manan-danja (tohatra, efitra fidiovana, fivoahana)?"
        },
        why: {
          fr: "Des repères clairs et un bon éclairage évitent les chutes et la désorientation, surtout la nuit et pour les personnes âgées.",
          en: "Clear markers and good lighting prevent falls and disorientation, especially at night and for the elderly.",
          pt: "Marcas claras e boa iluminação evitam quedas e desorientação, sobretudo à noite e para os idosos.",
          mg: "Ny famantarana mazava sy ny jiro tsara dia misoroka fianjerana sy fahaverezan-kevitra, indrindra amin'ny alina sy ho an'ny zokiolona."
        },
        what: {
          fr: "Couloirs et escaliers sombres la nuit, absence de veilleuses, indications peu visibles.",
          en: "Dark corridors and stairs at night, no night lights, hard-to-see signs.",
          pt: "Corredores e escadas escuros à noite, ausência de luzes de presença, indicações pouco visíveis.",
          mg: "Lalantsara sy tohatra maizina amin'ny alina, tsy misy jiro alina, famantarana saro-jerena."
        }
      }
    ]
  },
  {
    thematique: 'environnement',
    intro: {
      fr: "Le confort, la santé et les économies d'énergie au quotidien.",
      en: "Daily comfort, health and energy savings.",
      pt: "O conforto, a saúde e a poupança de energia no dia a dia.",
      mg: "Ny fahazoana aina, ny fahasalamana sy ny fitsitsiana angovo isan'andro."
    },
    questions: [
      {
        key: 'env_humid_01',
        image: 'environnement/moisissure.jpg',
        question: {
          fr: "Sentez-vous une odeur de moisi ou voyez-vous des taches noires de moisissure sur les murs ?",
          en: "Do you smell mustiness or see black mould stains on the walls?",
          pt: "Sente cheiro a mofo ou vê manchas pretas de bolor nas paredes?",
          mg: "Manimbolo lo ve ianao na mahita pentina mainty bobongolo eo amin'ny rindrina?"
        },
        why: {
          fr: "L'humidité et les moisissures nuisent à la santé (allergies, respiration) et dégradent le bâti.",
          en: "Damp and mould harm health (allergies, breathing) and degrade the structure.",
          pt: "A humidade e o bolor prejudicam a saúde (alergias, respiração) e degradam o edifício.",
          mg: "Ny hamandoana sy ny bobongolo dia manimba ny fahasalamana (alergie, fofonaina) ary manimba ny trano."
        },
        what: {
          fr: "Les angles de pièces, derrière les meubles, les salles d'eau, les chambres peu ventilées.",
          en: "Room corners, behind furniture, bathrooms, poorly ventilated bedrooms.",
          pt: "Os cantos das divisões, atrás dos móveis, casas de banho, quartos pouco ventilados.",
          mg: "Ny zoron'ny efitrano, ao ambadiky ny fanaka, ny efitra fandroana, ny efitrano tsy misy rivotra firy."
        }
      },
      {
        key: 'env_froid_01',
        image: 'environnement/isolation.jpg',
        question: {
          fr: "Y a-t-il des pièces inconfortables toute l'année — trop chaudes, trop froides ou pleines de courants d'air ?",
          en: "Are there rooms uncomfortable all year — too hot, too cold or full of draughts?",
          pt: "Há divisões desconfortáveis o ano todo — demasiado quentes, demasiado frias ou cheias de correntes de ar?",
          mg: "Misy efitrano tsy mampahazo aina mandritra ny taona — mafana loatra, mangatsiaka loatra na feno rivotra mitsoka ve?"
        },
        why: {
          fr: "Une mauvaise isolation rend les pièces inconfortables (chaleur ou froid) et fait grimper les factures d'énergie.",
          en: "Poor insulation makes rooms uncomfortable (heat or cold) and drives up energy bills.",
          pt: "Um mau isolamento torna as divisões desconfortáveis (calor ou frio) e aumenta as faturas de energia.",
          mg: "Ny isolation ratsy dia mahatonga ny efitrano tsy mampahazo aina (mafana na mangatsiaka) ary mampiakatra ny faktiora angovo."
        },
        what: {
          fr: "Les fenêtres en simple vitrage, les toitures/combles non isolés, les courants d'air sous les portes.",
          en: "Single-glazed windows, uninsulated roofs/attics, draughts under doors.",
          pt: "Janelas de vidro simples, telhados/sótãos sem isolamento, correntes de ar sob as portas.",
          mg: "Ny varavarankely vitre tokana, ny tafo/combles tsy voaisolant, ny rivotra ambanin'ny varavarana."
        }
      },
      {
        key: 'env_energie_01',
        image: 'environnement/facture.jpg',
        question: {
          fr: "Vos factures d'énergie (chauffage, électricité) vous semblent-elles anormalement élevées ?",
          en: "Do your energy bills (heating, electricity) seem unusually high?",
          pt: "As suas faturas de energia (aquecimento, eletricidade) parecem-lhe anormalmente altas?",
          mg: "Toa avo tsy ara-dalàna ve ny faktioranao angovo (fanafanana, herinaratra)?"
        },
        why: {
          fr: "Des factures élevées signalent souvent un gisement d'économies (isolation, régulation, équipements anciens).",
          en: "High bills often point to savings potential (insulation, controls, old equipment).",
          pt: "Faturas altas indicam muitas vezes potencial de poupança (isolamento, regulação, equipamentos antigos).",
          mg: "Ny faktiora avo matetika dia maneho fahafahana mitsitsy (isolation, fanaraha-maso, fitaovana tranainy)."
        },
        what: {
          fr: "L'évolution des factures d'une année sur l'autre, les équipements de chauffage anciens.",
          en: "How bills change year to year, old heating equipment.",
          pt: "A evolução das faturas de ano para ano, os equipamentos de aquecimento antigos.",
          mg: "Ny fivoaran'ny faktiora isan-taona, ny fitaovana fanafanana tranainy."
        }
      },
      {
        key: 'env_gel_01',
        image: 'environnement/gel-canalisation.jpg',
        question: {
          fr: "Le climat local (fortes chaleurs, fortes pluies, tempêtes, gel) a-t-il déjà causé des dégâts au bâtiment ?",
          en: "Has the local climate (extreme heat, heavy rain, storms, frost) ever damaged the building?",
          pt: "O clima local (calor intenso, chuvas fortes, tempestades, geada) já causou danos ao edifício?",
          mg: "Efa niteraka fahasimbana tamin'ny trano ve ny toetrandro eo an-toerana (hafanana be, orana be, tafio-drivotra, fanala)?"
        },
        why: {
          fr: "Selon les régions, chaleur, pluies, tempêtes ou gel peuvent abîmer la toiture, les murs ou les canalisations.",
          en: "Depending on the region, heat, rain, storms or frost can damage the roof, walls or pipes.",
          pt: "Consoante a região, calor, chuvas, tempestades ou geada podem danificar o telhado, as paredes ou os canos.",
          mg: "Arakaraka ny faritra, ny hafanana, ny orana, ny tafio-drivotra na ny fanala dia mety manimba ny tafo, ny rindrina na ny fantsona."
        },
        what: {
          fr: "Toiture endommagée après une tempête, canalisations éclatées par le gel, façades dégradées par le soleil ou la pluie.",
          en: "Roof damaged after a storm, pipes burst by frost, façades degraded by sun or rain.",
          pt: "Telhado danificado após tempestade, canos rebentados pela geada, fachadas degradadas pelo sol ou chuva.",
          mg: "Tafo simba aorian'ny tafio-drivotra, fantsona vaky noho ny fanala, façade simba noho ny masoandro na ny orana."
        }
      }
    ]
  },
  {
    thematique: 'assurance',
    intro: {
      fr: "Vérifions ensemble que le patrimoine est bien protégé sur le plan des assurances.",
      en: "Let's check together that the property is well covered by insurance.",
      pt: "Verifiquemos juntos se o património está bem protegido em termos de seguros.",
      mg: "Andeha hojerentsika miaraka raha voaaro tsara amin'ny fiantohana ny fananana."
    },
    questions: [
      {
        key: 'assur_mrh_01',
        image: 'assurance/contrat.jpg',
        question: {
          fr: "Le bâtiment est-il couvert par une assurance multirisques à jour ?",
          en: "Is the building covered by an up-to-date multi-risk insurance?",
          pt: "O edifício está coberto por um seguro multirriscos atualizado?",
          mg: "Voaaron'ny fiantohana multirisques mifanaraka amin'ny fotoana ve ny trano?"
        },
        why: {
          fr: "Sans assurance valide, un sinistre (incendie, dégât des eaux) peut représenter une perte financière énorme.",
          en: "Without valid insurance, a claim (fire, water damage) can be a huge financial loss.",
          pt: "Sem seguro válido, um sinistro (incêndio, danos de água) pode representar uma perda financeira enorme.",
          mg: "Raha tsy misy fiantohana manan-kery, ny loza (afo, simba rano) dia mety fatiantoka ara-bola lehibe."
        },
        what: {
          fr: "Le contrat d'assurance, la dernière quittance payée, la date d'échéance.",
          en: "The policy, the last paid receipt, the expiry date.",
          pt: "A apólice de seguro, o último recibo pago, a data de vencimento.",
          mg: "Ny fifanarahana fiantohana, ny resi-bola farany naloa, ny daty fahataperany."
        }
      },
      {
        key: 'assur_attest_01',
        image: 'assurance/attestation.jpg',
        question: {
          fr: "Disposez-vous d'une attestation d'assurance récente, facile à retrouver ?",
          en: "Do you have a recent insurance certificate that's easy to find?",
          pt: "Tem um certificado de seguro recente, fácil de encontrar?",
          mg: "Manana taratasy fanamarinana fiantohana vaovao mora hita ve ianao?"
        },
        why: {
          fr: "En cas de sinistre ou de contrôle, l'attestation à jour doit pouvoir être présentée rapidement.",
          en: "In a claim or audit, the current certificate must be quickly available.",
          pt: "Num sinistro ou inspeção, o certificado atualizado deve poder ser apresentado rapidamente.",
          mg: "Raha misy loza na fanaraha-maso, ny taratasy fanamarinana vaovao dia tokony haseho haingana."
        },
        what: {
          fr: "Le document daté de l'année en cours, au nom du bon bâtiment.",
          en: "A document dated this year, for the right building.",
          pt: "O documento datado do ano em curso, em nome do edifício correto.",
          mg: "Ny antontan-taratasy misy ny daty amin'ity taona ity, amin'ny anaran'ny trano marina."
        }
      },
      {
        key: 'assur_renouv_01',
        image: 'assurance/echeance.jpg',
        question: {
          fr: "Un contrat d'assurance arrive-t-il bientôt à échéance ou doit-il être renouvelé ?",
          en: "Is an insurance policy about to expire or due for renewal?",
          pt: "Algum contrato de seguro está prestes a vencer ou precisa de ser renovado?",
          mg: "Misy fifanarahana fiantohana efa ho lany na tokony havaozina ve?"
        },
        why: {
          fr: "Un contrat non renouvelé à temps peut laisser le bâtiment sans couverture.",
          en: "A policy not renewed in time can leave the building uncovered.",
          pt: "Um contrato não renovado a tempo pode deixar o edifício sem cobertura.",
          mg: "Ny fifanarahana tsy havaozina ara-potoana dia mety hahatonga ny trano tsy voaaro."
        },
        what: {
          fr: "Les dates d'échéance, les courriers de l'assureur.",
          en: "Expiry dates, letters from the insurer.",
          pt: "As datas de vencimento, as cartas da seguradora.",
          mg: "Ny daty fahataperany, ny taratasy avy amin'ny mpiantoka."
        }
      },
      {
        key: 'assur_decennale_01',
        image: 'assurance/decennale.jpg',
        question: {
          fr: "Si des travaux importants ont été réalisés récemment, disposez-vous des garanties écrites de l'entreprise ?",
          en: "If major works were done recently, do you have the contractor's written guarantees?",
          pt: "Se foram feitas obras importantes recentemente, tem as garantias escritas da empresa?",
          mg: "Raha nisy asa lehibe vita vao haingana, manana ny antoka an-tsoratra avy amin'ny orinasa ve ianao?"
        },
        why: {
          fr: "Les garanties de l'entreprise vous protègent contre les malfaçons après des travaux.",
          en: "The contractor's guarantees protect you against defects after works.",
          pt: "As garantias da empresa protegem-na contra defeitos após as obras.",
          mg: "Ny antoky ny orinasa dia miaro anao amin'ny asa ratsy aorian'ny asa."
        },
        what: {
          fr: "Les attestations remises par les entreprises lors des derniers chantiers.",
          en: "Certificates given by firms during recent projects.",
          pt: "Os certificados entregues pelas empresas nas últimas obras.",
          mg: "Ny taratasy fanamarinana nomen'ny orinasa nandritra ny asa farany."
        }
      }
    ]
  },
  {
    thematique: 'audits',
    intro: {
      fr: "Certains points méritent l'œil d'un professionnel. Faisons le point.",
      en: "Some points deserve a professional's eye. Let's take stock.",
      pt: "Alguns pontos merecem o olhar de um profissional. Façamos o balanço.",
      mg: "Misy zavatra mendrika ny hojeren'ny mpahay. Andeha hojerentsika."
    },
    questions: [
      {
        key: 'audit_struct_01',
        image: 'audits/expertise-structure.jpg',
        question: {
          fr: "Un doute persiste-t-il sur la solidité du bâtiment qui mériterait l'avis d'un expert ?",
          en: "Does a doubt remain about the building's soundness that warrants an expert's opinion?",
          pt: "Persiste alguma dúvida sobre a solidez do edifício que mereça a opinião de um perito?",
          mg: "Mbola misy fisalasalana momba ny fahamafisan'ny trano mila ny hevitry ny manam-pahaizana ve?"
        },
        why: {
          fr: "Quand un doute existe sur la structure, une expertise évite de laisser un problème grave s'installer.",
          en: "When there's doubt about the structure, an expert review prevents a serious problem from taking hold.",
          pt: "Quando há dúvida sobre a estrutura, uma peritagem evita deixar instalar-se um problema grave.",
          mg: "Rehefa misy fisalasalana momba ny rafitra, ny fanadihadiana dia misakana ny hipetrahan'ny olana lehibe."
        },
        what: {
          fr: "Les fissures inquiétantes, les affaissements, les zones évitées par prudence.",
          en: "Worrying cracks, sagging, areas avoided out of caution.",
          pt: "As fissuras preocupantes, os afundamentos, as zonas evitadas por prudência.",
          mg: "Ny triatra mampanahy, ny filentehana, ny faritra ialana noho ny fitandremana."
        }
      },
      {
        key: 'audit_elec_01',
        image: 'audits/diagnostic-elec.jpg',
        question: {
          fr: "Souhaiteriez-vous faire vérifier l'installation électrique par un professionnel pour être tranquille ?",
          en: "Would you like the electrical installation checked by a professional for peace of mind?",
          pt: "Gostaria de mandar verificar a instalação elétrica por um profissional para ficar tranquila?",
          mg: "Te-hampanamarina ny fametrahana elektrika amin'ny mpahay ve ianao mba hahazo aina?"
        },
        why: {
          fr: "Un diagnostic électrique lève le doute sur l'un des principaux risques d'un bâtiment.",
          en: "An electrical diagnosis removes doubt about one of a building's main risks.",
          pt: "Um diagnóstico elétrico esclarece a dúvida sobre um dos principais riscos de um edifício.",
          mg: "Ny diagnostic elektrika dia manaisotra ny fisalasalana momba ny iray amin'ny risika lehibe amin'ny trano."
        },
        what: {
          fr: "L'âge de l'installation, l'absence de contrôle récent.",
          en: "The age of the installation, no recent inspection.",
          pt: "A idade da instalação, a ausência de inspeção recente.",
          mg: "Ny taonan'ny fametrahana, ny tsy fisian'ny fanaraha-maso vao haingana."
        }
      },
      {
        key: 'audit_controle_01',
        image: 'audits/controle-reglementaire.jpg',
        question: {
          fr: "Y a-t-il des équipements importants (ascenseur, installation de gaz, sécurité incendie) dont vous ignorez l'état d'entretien ?",
          en: "Are there important systems (lift, gas installation, fire safety) whose maintenance status you don't know?",
          pt: "Há equipamentos importantes (elevador, instalação de gás, segurança contra incêndio) cujo estado de manutenção desconhece?",
          mg: "Misy fitaovana manan-danja (ascenseur, fametrahana gazy, fiarovana amin'ny afo) tsy fantatrao ny toetry ny fikojakojana azy ve?"
        },
        why: {
          fr: "Un équipement important mal entretenu peut devenir dangereux ; un contrôle par un professionnel lève le doute.",
          en: "A poorly maintained key system can become dangerous; a professional check removes the doubt.",
          pt: "Um equipamento importante mal mantido pode tornar-se perigoso; uma verificação por um profissional esclarece a dúvida.",
          mg: "Ny fitaovana manan-danja tsy voakojakoja tsara dia mety ho mampidi-doza; ny fanamarinana ataon'ny mpahay dia manaisotra ny fisalasalana."
        },
        what: {
          fr: "Les rapports de contrôle, les vignettes/étiquettes de vérification sur les équipements.",
          en: "Inspection reports, verification stickers/labels on equipment.",
          pt: "Os relatórios de inspeção, os selos/etiquetas de verificação nos equipamentos.",
          mg: "Ny tatitra fanaraha-maso, ny vignette/étiquette fanamarinana eo amin'ny fitaovana."
        }
      }
    ]
  },
  {
    thematique: 'statut_juridique',
    intro: {
      fr: "Quelques questions administratives sur la propriété du bien.",
      en: "A few administrative questions about ownership of the property.",
      pt: "Algumas perguntas administrativas sobre a propriedade do bem.",
      mg: "Fanontaniana ara-pitantanana vitsivitsy momba ny fananana ny fananana."
    },
    questions: [
      {
        key: 'juridique_titre_01',
        image: 'statut_juridique/titre-propriete.jpg',
        question: {
          fr: "Disposez-vous du titre de propriété (acte notarié) du bâtiment, facile à retrouver ?",
          en: "Do you have the property title (notarial deed), easy to find?",
          pt: "Tem o título de propriedade (escritura) do edifício, fácil de encontrar?",
          mg: "Manana ny taratasy fananana (acte notarié) ny trano, mora hita ve ianao?"
        },
        why: {
          fr: "Le titre de propriété est indispensable pour toute vente, travaux importants ou démarche administrative.",
          en: "The title is essential for any sale, major works or administrative step.",
          pt: "O título de propriedade é indispensável para qualquer venda, obras importantes ou diligência administrativa.",
          mg: "Ny taratasy fananana dia ilaina amin'ny varotra, asa lehibe na fombafomba ara-pitantanana."
        },
        what: {
          fr: "L'acte notarié d'achat ou de donation, conservé dans les archives.",
          en: "The notarial purchase or gift deed, kept in the archives.",
          pt: "A escritura de compra ou doação, guardada nos arquivos.",
          mg: "Ny acte notarié fividianana na fanomezana, voatahiry ao amin'ny arsiva."
        }
      },
      {
        key: 'juridique_litige_01',
        image: 'statut_juridique/litige.jpg',
        question: {
          fr: "Existe-t-il actuellement un désaccord, un conflit ou une procédure concernant ce bien (voisinage, limites, propriété) ?",
          en: "Is there currently a disagreement, conflict or proceeding about this property (neighbours, boundaries, ownership)?",
          pt: "Existe atualmente um desacordo, conflito ou processo relativo a este bem (vizinhança, limites, propriedade)?",
          mg: "Misy tsy fitovian-kevitra, ady na fizotran-draharaha momba ity fananana ity amin'izao (mpifanila, faritra, fananana) ve?"
        },
        why: {
          fr: "Un litige en cours peut bloquer des travaux ou une vente et doit être suivi.",
          en: "An ongoing dispute can block works or a sale and must be tracked.",
          pt: "Um litígio em curso pode bloquear obras ou uma venda e deve ser acompanhado.",
          mg: "Ny ady mbola misy dia mety hanakana asa na varotra ary tokony harahina."
        },
        what: {
          fr: "Les courriers d'avocat, les conflits de mitoyenneté, les contestations de limites.",
          en: "Lawyer letters, boundary disputes, limit contestations.",
          pt: "As cartas de advogado, os conflitos de meação, as contestações de limites.",
          mg: "Ny taratasin'ny mpisolovava, ny ady mitoyenneté, ny fanoherana faritra."
        }
      },
      {
        key: 'juridique_revend_01',
        image: 'statut_juridique/revendication.jpg',
        question: {
          fr: "Quelqu'un a-t-il récemment revendiqué un droit sur ce bien (héritage, occupation, servitude) ?",
          en: "Has anyone recently claimed a right over this property (inheritance, occupation, easement)?",
          pt: "Alguém reivindicou recentemente um direito sobre este bem (herança, ocupação, servidão)?",
          mg: "Nisy olona nitaky zo tamin'ity fananana ity vao haingana ve (lova, fonenana, servitude)?"
        },
        why: {
          fr: "Une revendication récente peut fragiliser la propriété et mérite une vérification juridique.",
          en: "A recent claim can weaken ownership and warrants legal review.",
          pt: "Uma reivindicação recente pode fragilizar a propriedade e merece verificação jurídica.",
          mg: "Ny fitakiana vao haingana dia mety hampalemy ny fananana ary mila fanamarinana ara-dalàna."
        },
        what: {
          fr: "Les courriers récents, les demandes de tiers, les passages revendiqués.",
          en: "Recent letters, third-party requests, claimed rights of way.",
          pt: "As cartas recentes, os pedidos de terceiros, as passagens reivindicadas.",
          mg: "Ny taratasy vao haingana, ny fangatahan'ny hafa, ny lalana notakiana."
        }
      }
    ]
  },
  {
    thematique: 'patrimoine_protege',
    intro: {
      fr: "Si le bâtiment a une valeur historique, certaines précautions s'imposent.",
      en: "If the building has historic value, certain precautions apply.",
      pt: "Se o edifício tem valor histórico, certas precauções impõem-se.",
      mg: "Raha manana lanja ara-tantara ny trano, misy fitandremana tsy maintsy atao."
    },
    questions: [
      {
        key: 'protege_classe_01',
        image: 'patrimoine_protege/monument-historique.jpg',
        question: {
          fr: "Le bâtiment, la chapelle ou une partie est-il classé ou protégé au titre du patrimoine (par une autorité locale) ?",
          en: "Is the building, chapel or a part listed or protected as heritage (by a local authority)?",
          pt: "O edifício, a capela ou uma parte está classificado ou protegido como património (por uma autoridade local)?",
          mg: "Voasokajy na arovana ho lova ara-tantara (avy amin'ny manampahefana eo an-toerana) ve ny trano, ny chapelle na ny ampahany?"
        },
        why: {
          fr: "Un bien protégé impose des règles particulières pour tout travaux ; les ignorer expose à des sanctions.",
          en: "A protected property imposes special rules for any works; ignoring them risks penalties.",
          pt: "Um bem protegido impõe regras especiais para quaisquer obras; ignorá-las expõe a sanções.",
          mg: "Ny fananana voaaro dia mametraka fitsipika manokana ho an'ny asa rehetra; ny tsy fiheverana azy dia mampidi-tsazy."
        },
        what: {
          fr: "Les documents de classement, une plaque ou une mention de protection patrimoniale.",
          en: "Listing documents, a plaque or a heritage-protection mention.",
          pt: "Os documentos de classificação, uma placa ou menção de proteção patrimonial.",
          mg: "Ny antontan-taratasy fanasokajiana, takelaka na soratra fiarovana ara-tantara."
        }
      },
      {
        key: 'protege_facade_01',
        image: 'patrimoine_protege/facade-protegee.jpg',
        question: {
          fr: "Les façades ou éléments visibles font-ils l'objet d'une protection particulière (zone protégée, avis d'une autorité du patrimoine) ?",
          en: "Are the façades or visible elements under special protection (protected zone, heritage authority opinion)?",
          pt: "As fachadas ou elementos visíveis estão sob proteção especial (zona protegida, parecer do património)?",
          mg: "Voaaro manokana ve ny façade na ny singa hita maso (faritra voaaro, hevitry ny manampahefana misahana ny lova)?"
        },
        why: {
          fr: "En zone protégée, modifier une façade nécessite des autorisations spécifiques.",
          en: "In a protected zone, altering a façade needs specific permits.",
          pt: "Numa zona protegida, alterar uma fachada exige autorizações específicas.",
          mg: "Ao amin'ny faritra voaaro, ny fanovana façade dia mila fahazoan-dàlana manokana."
        },
        what: {
          fr: "Les contraintes connues lors de précédents travaux, la situation près d'un monument.",
          en: "Constraints known from past works, location near a monument.",
          pt: "As restrições conhecidas de obras anteriores, a localização perto de um monumento.",
          mg: "Ny famerana fantatra tamin'ny asa teo aloha, ny toerana akaikin'ny monument."
        }
      },
      {
        key: 'protege_interieur_01',
        image: 'patrimoine_protege/interieur-classe.jpg',
        question: {
          fr: "Des éléments intérieurs (vitraux, boiseries, orgue, décors) ont-ils une valeur patrimoniale à préserver ?",
          en: "Do interior elements (stained glass, woodwork, organ, décor) have heritage value to preserve?",
          pt: "Há elementos interiores (vitrais, madeiras, órgão, decorações) com valor patrimonial a preservar?",
          mg: "Misy singa anatiny (vitraux, hazo, orgue, haingo) manana lanja ara-pomban-drazana tokony harovana ve?"
        },
        why: {
          fr: "Des éléments classés à l'intérieur doivent être protégés et restaurés par des spécialistes.",
          en: "Listed interior elements must be protected and restored by specialists.",
          pt: "Elementos interiores classificados devem ser protegidos e restaurados por especialistas.",
          mg: "Ny singa anatiny voasokajy dia tsy maintsy arovana sy amboarin'ny manam-pahaizana manokana."
        },
        what: {
          fr: "Vitraux anciens, statues, mobilier liturgique, décors peints.",
          en: "Old stained glass, statues, liturgical furniture, painted décor.",
          pt: "Vitrais antigos, estátuas, mobiliário litúrgico, decorações pintadas.",
          mg: "Vitraux tranainy, sary sokitra, fanaka litorzika, haingo voahosotra."
        }
      }
    ]
  }
];

// Libellés d'interface du questionnaire (4 langues, repli français via localized()).
// Regroupés ici pour garder la fonctionnalité autonome et faciliter la relecture.
export const UI = {
  panelTitle: { fr: 'Diagnostic du bâtiment', en: 'Building check-up', pt: 'Diagnóstico do edifício', mg: 'Fanadihadiana ny trano' },
  panelSubtitle: {
    fr: "Repérez les points qui méritent attention et créez des investissements pour les corriger.",
    en: 'Spot the points that need attention and create investments to fix them.',
    pt: 'Identifique os pontos a vigiar e crie investimentos para os corrigir.',
    mg: "Fantaro ny zavatra mila jerena ary mamorona fampiasam-bola hanitsiana azy."
  },
  start: { fr: 'Lancer un diagnostic', en: 'Start a check-up', pt: 'Iniciar um diagnóstico', mg: 'Manomboka fanadihadiana' },
  saveSiteFirst: {
    fr: "Enregistrez d'abord le site pour pouvoir lancer un diagnostic.",
    en: 'Save the site first to be able to start a check-up.',
    pt: 'Guarde primeiro o site para poder iniciar um diagnóstico.',
    mg: 'Tehirizo aloha ny toerana vao afaka manomboka fanadihadiana.'
  },
  noAssessments: { fr: 'Aucun diagnostic pour le moment.', en: 'No check-up yet.', pt: 'Ainda sem diagnósticos.', mg: 'Mbola tsy misy fanadihadiana.' },
  resume: { fr: 'Reprendre', en: 'Resume', pt: 'Retomar', mg: 'Manohy' },
  viewAnswers: { fr: 'Voir les réponses', en: 'View answers', pt: 'Ver respostas', mg: 'Hijery ny valiny' },
  exportPdf: { fr: 'Exporter en PDF', en: 'Export as PDF', pt: 'Exportar em PDF', mg: 'Hamoaka ho PDF' },
  exportPdfError: {
    fr: "Impossible de générer le PDF du diagnostic.",
    en: 'Unable to generate the check-up PDF.',
    pt: 'Não foi possível gerar o PDF do diagnóstico.',
    mg: 'Tsy afaka namorona ny PDF an’ny fanadihadiana.'
  },
  delete: { fr: 'Supprimer', en: 'Delete', pt: 'Eliminar', mg: 'Hamafa' },
  deleteConfirm: { fr: 'Supprimer ce diagnostic ?', en: 'Delete this check-up?', pt: 'Eliminar este diagnóstico?', mg: 'Hofafana ity fanadihadiana ity?' },
  reset: { fr: 'Réinitialiser', en: 'Reset', pt: 'Reiniciar', mg: 'Averina amin’ny voalohany' },
  resetTitle: { fr: 'Réinitialiser le diagnostic', en: 'Reset the check-up', pt: 'Reiniciar o diagnóstico', mg: 'Hamerina ny fanadihadiana' },
  resetText: {
    fr: 'Toutes les réponses de ce diagnostic seront effacées et le questionnaire recommencera à zéro.',
    en: 'All answers of this check-up will be erased and the questionnaire will start over.',
    pt: 'Todas as respostas deste diagnóstico serão apagadas e o questionário recomeçará do zero.',
    mg: 'Ho voafafa daholo ny valin’ity fanadihadiana ity ary hiverina amin’ny voalohany ny fanontaniana.'
  },
  resetDeleteWarning: {
    fr: '{n} investissement(s) créé(s) par ce diagnostic vont être définitivement supprimés, avec leurs budgets et pièces jointes :',
    en: '{n} investment(s) created by this check-up will be permanently deleted, along with their budgets and attachments:',
    pt: '{n} investimento(s) criado(s) por este diagnóstico serão eliminados definitivamente, com os seus orçamentos e anexos:',
    mg: 'Ho voafafa tanteraka ireo fampiasam-bola {n} noforonin’ity fanadihadiana ity, miaraka amin’ny teti-bolany sy ny rakitra miaraka aminy:'
  },
  resetDeleteButton: {
    fr: 'Réinitialiser et supprimer {n} investissement(s)',
    en: 'Reset and delete {n} investment(s)',
    pt: 'Reiniciar e eliminar {n} investimento(s)',
    mg: 'Hamerina sy hamafa fampiasam-bola {n}'
  },
  saveBeforeTitle: { fr: 'Enregistrement du formulaire', en: 'Saving the form', pt: 'Gravação do formulário', mg: 'Fitahirizana ny taratasy' },
  saveBeforeText: {
    fr: 'Le formulaire du site va être enregistré avant de lancer le diagnostic, pour ne perdre aucune donnée.',
    en: 'The site form will be saved before starting the check-up, so that no data is lost.',
    pt: 'O formulário do site será gravado antes de iniciar o diagnóstico, para não perder nenhum dado.',
    mg: 'Hotahirizina aloha ny taratasin’ny toerana alohan’ny hanombohana ny fanadihadiana, mba tsy hisy angona ho very.'
  },
  saveAndContinue: { fr: 'Enregistrer et continuer', en: 'Save and continue', pt: 'Gravar e continuar', mg: 'Hitahiry ary hanohy' },
  cancel: { fr: 'Annuler', en: 'Cancel', pt: 'Cancelar', mg: 'Hanafoana' },
  launchError: { fr: "Impossible d'ouvrir le diagnostic.", en: 'Unable to open the check-up.', pt: 'Não foi possível abrir o diagnóstico.', mg: 'Tsy afaka nosokafana ny fanadihadiana.' },
  startError: { fr: 'Erreur lors du démarrage du diagnostic.', en: 'Error while starting the check-up.', pt: 'Erro ao iniciar o diagnóstico.', mg: "Nisy olana tamin'ny fanombohana ny fanadihadiana." },
  completeError: { fr: 'Erreur lors du calcul des propositions.', en: 'Error while calculating the proposals.', pt: 'Erro ao calcular as propostas.', mg: "Nisy olana tamin'ny fikajiana ireo tolo-kevitra." },
  acceptError: { fr: 'Erreur lors de la création des investissements.', en: 'Error while creating the investments.', pt: 'Erro ao criar os investimentos.', mg: "Nisy olana tamin'ny famoronana ireo fampiasam-bola." },
  loadInvestmentsError: {
    fr: 'Impossible de charger les investissements liés à ce diagnostic.',
    en: 'Unable to load the investments linked to this check-up.',
    pt: 'Não foi possível carregar os investimentos ligados a este diagnóstico.',
    mg: 'Tsy afaka nampiditra ireo fampiasam-bola mifandray amin’ity fanadihadiana ity.'
  },
  alreadyExistsTitle: { fr: 'Déjà dans vos investissements', en: 'Already in your investments', pt: 'Já nos seus investimentos', mg: 'Efa ao amin’ny fampiasam-bolanao' },
  alreadyExistsHint: {
    fr: 'Ces points ont déjà un investissement sur ce périmètre : ils ne seront pas recréés.',
    en: 'These points already have an investment for this scope: they will not be created again.',
    pt: 'Estes pontos já têm um investimento neste perímetro: não serão criados novamente.',
    mg: 'Efa manana fampiasam-bola amin’ity faritra ity ireo teboka ireo: tsy hoforonina indray izy ireo.'
  },
  alreadyExists: { fr: 'Investissement déjà existant', en: 'Investment already exists', pt: 'Investimento já existente', mg: 'Fampiasam-bola efa misy' },
  alreadyExistsRejected: {
    fr: 'Investissement déjà existant (rejeté le {date})',
    en: 'Investment already exists (rejected on {date})',
    pt: 'Investimento já existente (rejeitado em {date})',
    mg: 'Fampiasam-bola efa misy (nolavina tamin’ny {date})'
  },
  skippedToast: {
    fr: '{n} proposition(s) ignorée(s) : investissement déjà existant.',
    en: '{n} proposal(s) skipped: investment already exists.',
    pt: '{n} proposta(s) ignorada(s): investimento já existente.',
    mg: 'Tolo-kevitra {n} tsy noraisina: efa misy ny fampiasam-bola.'
  },
  resetError: { fr: 'Impossible de réinitialiser le diagnostic.', en: 'Unable to reset the check-up.', pt: 'Não foi possível reiniciar o diagnóstico.', mg: 'Tsy afaka naverina ny fanadihadiana.' },
  statusDraft: { fr: 'En cours', en: 'In progress', pt: 'Em curso', mg: 'Eo am-panaovana' },
  statusCompleted: { fr: 'Terminé', en: 'Completed', pt: 'Concluído', mg: 'Vita' },
  scopeAllSite: { fr: 'Tout le site', en: 'The whole site', pt: 'Todo o site', mg: "Ny toerana manontolo" },
  scopeBuilding: { fr: 'Un bâtiment précis', en: 'A specific building', pt: 'Um edifício específico', mg: 'Trano iray manokana' },
  building: { fr: 'Bâtiment', en: 'Building', pt: 'Edifício', mg: 'Trano' },
  welcomeTitle: { fr: 'Faisons le point sur votre bâtiment', en: "Let's check up on your building", pt: 'Vamos avaliar o seu edifício', mg: 'Andeha hojerentsika ny tranonao' },
  welcomeText: {
    fr: "Ce questionnaire vous aide à repérer ce qui mérite attention. Pas besoin d'être experte : répondez simplement à ce que vous observez. Vous pouvez vous arrêter et reprendre à tout moment. Comptez environ 10 minutes.",
    en: 'This questionnaire helps you spot what needs attention. No need to be an expert: just answer based on what you see. You can stop and resume any time. It takes about 10 minutes.',
    pt: 'Este questionário ajuda-a a identificar o que merece atenção. Não precisa de ser perita: responda apenas ao que observa. Pode parar e retomar a qualquer momento. São cerca de 10 minutos.',
    mg: "Ity fanontaniana ity dia manampy anao hahita izay mila jerena. Tsy mila ho manam-pahaizana ianao: valio fotsiny izay hitanao. Afaka mijanona sy manohy amin'ny fotoana rehetra ianao. Maharitra 10 minitra eo ho eo."
  },
  chooseScope: { fr: 'Que souhaitez-vous évaluer ?', en: 'What would you like to assess?', pt: 'O que deseja avaliar?', mg: 'Inona no tianao hodinihina?' },
  begin: { fr: 'Commencer', en: 'Begin', pt: 'Começar', mg: 'Manomboka' },
  questionOf: { fr: 'Question {c} sur {t}', en: 'Question {c} of {t}', pt: 'Pergunta {c} de {t}', mg: "Fanontaniana {c} amin'ny {t}" },
  previous: { fr: 'Précédent', en: 'Previous', pt: 'Anterior', mg: 'Aloha' },
  next: { fr: 'Suivant', en: 'Next', pt: 'Seguinte', mg: 'Manaraka' },
  finish: { fr: 'Voir le bilan', en: 'See the summary', pt: 'Ver o resumo', mg: 'Hijery ny famintinana' },
  whyTitle: { fr: "Pourquoi c'est important", en: 'Why it matters', pt: 'Porque é importante', mg: 'Nahoana no zava-dehibe' },
  whatTitle: { fr: 'Quoi regarder', en: 'What to look at', pt: 'O que observar', mg: 'Inona no jerena' },
  resultsTitle: { fr: 'Voici ce que nous avons repéré ensemble', en: "Here's what we spotted together", pt: 'Eis o que identificámos juntas', mg: 'Ireto izay hitantsika niaraka' },
  allGoodTitle: { fr: 'Tout semble en ordre !', en: 'Everything looks fine!', pt: 'Está tudo em ordem!', mg: 'Toa milamina daholo!' },
  allGoodText: {
    fr: "Vous n'avez signalé aucun point d'attention. Continuez ainsi et refaites un diagnostic de temps en temps.",
    en: 'You reported no points of concern. Keep it up and run a check-up now and then.',
    pt: 'Não assinalou nenhum ponto de atenção. Continue assim e faça um diagnóstico de vez em quando.',
    mg: "Tsy nisy zavatra nampahatsiahy anao. Tohizo ary manaova fanadihadiana indraindray."
  },
  bucketUrgent: { fr: 'À traiter rapidement', en: 'Address quickly', pt: 'Tratar rapidamente', mg: 'Hokarakaraina haingana' },
  bucketImportant: { fr: 'Important', en: 'Important', pt: 'Importante', mg: 'Manan-danja' },
  bucketWatch: { fr: 'À surveiller', en: 'To monitor', pt: 'A vigiar', mg: 'Harahi-maso' },
  addToInvestments: { fr: 'Ajouter à mes investissements', en: 'Add to my investments', pt: 'Adicionar aos meus investimentos', mg: "Ampio amin'ny fampiasam-bolako" },
  createSelected: { fr: 'Créer les investissements sélectionnés', en: 'Create selected investments', pt: 'Criar os investimentos selecionados', mg: 'Mamorona ny fampiasam-bola voafidy' },
  createdToast: { fr: '{n} investissement(s) ajouté(s) à votre liste', en: '{n} investment(s) added to your list', pt: '{n} investimento(s) adicionado(s) à sua lista', mg: "Fampiasam-bola {n} nampiana tao amin'ny lisitrao" },
  nothingSelected: { fr: 'Aucune proposition sélectionnée', en: 'No proposal selected', pt: 'Nenhuma proposta selecionada', mg: 'Tsy misy tolo-kevitra voafidy' },
  close: { fr: 'Fermer', en: 'Close', pt: 'Fechar', mg: 'Hidiana' },
  cancel: { fr: 'Annuler', en: 'Cancel', pt: 'Cancelar', mg: 'Hanafoana' },
  answersSection: { fr: 'Vos réponses', en: 'Your answers', pt: 'As suas respostas', mg: 'Ny valinao' },
  proposalsSection: { fr: "Propositions d'investissement", en: 'Investment proposals', pt: 'Propostas de investimento', mg: 'Tolo-kevitra fampiasam-bola' },
  noProposalsReview: { fr: 'Aucune proposition générée.', en: 'No proposal generated.', pt: 'Nenhuma proposta gerada.', mg: 'Tsy nisy tolo-kevitra noforonina.' },
  recommended: { fr: 'Recommandé', en: 'Recommended', pt: 'Recomendado', mg: 'Aroso' },
  quit: { fr: 'Quitter', en: 'Quit', pt: 'Sair', mg: 'Hiala' },
  quitConfirm: {
    fr: 'Vos réponses sont enregistrées comme brouillon. Vous pourrez reprendre plus tard. Quitter ?',
    en: 'Your answers are saved as a draft. You can resume later. Quit?',
    pt: 'As suas respostas ficam guardadas como rascunho. Pode retomar depois. Sair?',
    mg: "Voatahiry ho brouillon ny valinao. Afaka manohy aoriana ianao. Hiala?"
  }
};

// Index plat des questions par clé (pratique pour l'affichage des résultats/relectures)
export const QUESTIONS_BY_KEY = CHAPTERS.reduce((acc, ch) => {
  ch.questions.forEach((q) => {
    acc[q.key] = { ...q, thematique: ch.thematique };
  });
  return acc;
}, {});

// Helper d'affichage : renvoie le texte dans la langue courante avec repli sur le français
export function localized(obj, lang) {
  if (!obj) return '';
  return obj[lang] || obj.fr || '';
}
